import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Building2, Search, Plus, Pencil, Trash2,
  Loader2, X, AlertCircle, Users, ArrowLeft
} from "lucide-react";
import { supabase } from "../../lib/supabaseClient";
import { useToast } from "../ui/Toast";
import ConfirmDialog from "../ui/ConfirmDialog";

const AdminColleges = () => {
  const navigate = useNavigate();
  const toast = useToast();

  const [colleges, setColleges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");

  const [showModal, setShowModal] = useState(false);
  const [editingCollege, setEditingCollege] = useState(null);
  const [modalLoading, setModalLoading] = useState(false);
  const [formName, setFormName] = useState("");
  const [formError, setFormError] = useState("");

  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteStudentCount, setDeleteStudentCount] = useState(0);

  const fetchColleges = useCallback(async () => {
    setLoading(true);

    const { data: collegeData, error } = await supabase
      .from("colleges")
      .select("*")
      .order("name");

    if (error) {
      toast.error("Failed to load colleges");
      setLoading(false);
      return;
    }

    const { data: volunteers } = await supabase
      .from("volunteers")
      .select("college_name");

    const countMap = {};
    (volunteers || []).forEach((v) => {
      const key = v.college_name;
      countMap[key] = (countMap[key] || 0) + 1;
    });

    const enriched = (collegeData || []).map((c) => ({
      ...c,
      studentCount: countMap[c.name] || 0,
    }));

    setColleges(enriched);
    setLoading(false);
  }, [toast]);

  useEffect(() => {
    fetchColleges();
  }, [fetchColleges]);

  const filteredColleges = colleges.filter((c) => {
    if (searchQuery) {
      return c.name?.toLowerCase().includes(searchQuery.toLowerCase());
    }
    return true;
  });

  const totalStudents = colleges.reduce((sum, c) => sum + c.studentCount, 0);

  const openAddModal = () => {
    setEditingCollege(null);
    setFormName("");
    setFormError("");
    setShowModal(true);
  };

  const openEditModal = (college) => {
    setEditingCollege(college);
    setFormName(college.name);
    setFormError("");
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!formName.trim()) {
      setFormError("College name is required");
      return;
    }

    const duplicate = colleges.find(
      (c) =>
        c.name.toLowerCase() === formName.trim().toLowerCase() &&
        c.id !== editingCollege?.id
    );
    if (duplicate) {
      setFormError("A college with this name already exists");
      return;
    }

    setModalLoading(true);

    if (editingCollege) {
      const oldName = editingCollege.name;
      const newName = formName.trim();

      const { error } = await supabase
        .from("colleges")
        .update({ name: newName })
        .eq("id", editingCollege.id);

      if (error) {
        toast.error("Failed to update: " + error.message);
      } else {
        if (oldName !== newName) {
          await supabase
            .from("volunteers")
            .update({ college_name: newName })
            .eq("college_name", oldName);
        }
        toast.success("College updated successfully");
        setShowModal(false);
        fetchColleges();
      }
    } else {
      const { error } = await supabase
        .from("colleges")
        .insert({ name: formName.trim() });

      if (error) {
        toast.error("Failed to add: " + error.message);
      } else {
        toast.success("College added successfully");
        setShowModal(false);
        fetchColleges();
      }
    }
    setModalLoading(false);
  };

  const openDeleteConfirm = async (college) => {
    const { count } = await supabase
      .from("volunteers")
      .select("id", { count: "exact", head: true })
      .eq("college_name", college.name);

    setDeleteStudentCount(count || 0);
    setDeleteTarget(college);
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleteLoading(true);

    const { error } = await supabase
      .from("colleges")
      .delete()
      .eq("id", deleteTarget.id);

    if (error) {
      toast.error("Failed to delete: " + error.message);
    } else {
      toast.success("College deleted");
      fetchColleges();
    }
    setDeleteTarget(null);
    setDeleteLoading(false);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "—";
    return new Date(dateStr).toLocaleDateString("en-IN", {
      month: "short", day: "2-digit", year: "numeric",
    });
  };

  return (
    <div className="min-h-screen bg-ku-cream">
      {/* Header */}
      <div className="bg-gradient-to-br from-[#1a1a2e] via-[#2d1b4e] to-[#3B1E60] text-white py-8 md:py-10 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div>
              <button
                type="button"
                onClick={() => navigate("/admin/dashboard")}
                className="inline-flex items-center gap-1.5 text-xs font-bold text-ku-gold hover:text-amber-300 transition-colors bg-transparent border-none cursor-pointer mb-3 p-0"
              >
                <ArrowLeft className="w-4 h-4" />
                Back to Dashboard
              </button>
              <h1 className="text-2xl sm:text-3xl font-black tracking-tight">College Management</h1>
              <p className="text-white/60 text-sm mt-1 font-medium">{colleges.length} colleges • {totalStudents} total students</p>
            </div>
            <button
              type="button"
              onClick={openAddModal}
              className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-ku-gold text-ku-purple-deep text-sm font-bold hover:bg-amber-400 transition-colors cursor-pointer shadow-sm border-none self-start sm:self-auto"
            >
              <Plus className="w-4 h-4" /> Add College
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 py-6">
        <div className="bg-white rounded-2xl border border-ku-soft/50 shadow-sm p-4 mb-6">
          <div className="relative max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search colleges..."
              className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-gray-200 text-sm font-bold text-ku-purple bg-ku-cream/30 focus:border-ku-purple outline-none"
            />
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 text-ku-purple animate-spin" />
          </div>
        ) : filteredColleges.length === 0 ? (
          <div className="bg-white rounded-2xl border border-purple-100 shadow-sm flex flex-col items-center justify-center py-20 text-gray-400">
            <Building2 className="w-12 h-12 mb-3 text-purple-300" />
            <p className="font-bold text-gray-500">No colleges found</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredColleges.map((college, index) => (
              <motion.div
                key={college.id}
                initial={{ opacity: 0, y: 15 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: index * 0.05 }}
                className="bg-white rounded-2xl border border-purple-100 shadow-sm p-5 hover:shadow-elevated transition-all group"
              >
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-ku-purple to-ku-violet flex items-center justify-center text-white shadow-sm">
                      <Building2 className="w-5 h-5" />
                    </div>
                    <div>
                      <h3 className="font-black text-gray-900 text-sm tracking-tight">{college.name}</h3>
                      <p className="text-xs text-gray-400 font-medium mt-0.5">Added {formatDate(college.created_at)}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    <button
                      type="button"
                      onClick={() => openEditModal(college)}
                      className="p-1.5 rounded-lg bg-blue-50 text-blue-600 hover:bg-blue-100 border-none cursor-pointer"
                    >
                      <Pencil className="w-3.5 h-3.5" />
                    </button>
                    <button
                      type="button"
                      onClick={() => openDeleteConfirm(college)}
                      className="p-1.5 rounded-lg bg-red-50 text-red-500 hover:bg-red-100 border-none cursor-pointer"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>

                <div className="mt-4 pt-4 border-t border-gray-100 flex items-center gap-2">
                  <Users className="w-4 h-4 text-ku-violet" />
                  <span className="text-sm font-bold text-ku-purple">{college.studentCount}</span>
                  <span className="text-xs text-gray-400 font-medium">{college.studentCount === 1 ? "student" : "students"}</span>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>

      {/* Add / Edit Modal */}
      <AnimatePresence>
        {showModal && (
          <>
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={() => setShowModal(false)} className="fixed inset-0 z-[9990] bg-black/40 backdrop-blur-sm" />
            <motion.div initial={{ opacity: 0, scale: 0.92, y: 20 }} animate={{ opacity: 1, scale: 1, y: 0 }} exit={{ opacity: 0, scale: 0.92, y: 20 }} className="fixed inset-0 z-[9991] flex items-center justify-center p-4">
              <div className="bg-white rounded-2xl shadow-elevated border border-ku-soft/60 w-full max-w-md overflow-hidden">
                <div className="flex items-center justify-between p-6 pb-4 border-b border-ku-soft/50">
                  <h3 className="text-lg font-black text-ku-purple tracking-tight">{editingCollege ? "Edit College" : "Add College"}</h3>
                  <button type="button" onClick={() => setShowModal(false)} className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 bg-transparent border-none cursor-pointer"><X className="w-4 h-4" /></button>
                </div>
                <div className="p-6">
                  <div className="space-y-1.5">
                    <label className="block text-xs font-black text-ku-purple uppercase tracking-wider opacity-80">College Name *</label>
                    <div className="relative">
                      <Building2 className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                      <input
                        type="text"
                        value={formName}
                        onChange={(e) => { setFormName(e.target.value); setFormError(""); }}
                        placeholder="e.g. Rizvi College of Engineering"
                        className="w-full pl-11 pr-4 py-3 rounded-xl border text-sm font-bold text-ku-purple outline-none bg-ku-cream/30 focus:bg-white"
                        onKeyDown={(e) => { if (e.key === "Enter") handleSave(); }}
                      />
                    </div>
                    {formError && <p className="text-xs font-bold text-red-500 flex items-center gap-1"><AlertCircle className="w-3 h-3" />{formError}</p>}
                  </div>
                </div>
                <div className="flex items-center justify-end gap-3 p-6 pt-0">
                  <button type="button" onClick={() => setShowModal(false)} className="px-5 py-2.5 rounded-xl text-sm font-bold text-gray-600 bg-gray-100 border-none cursor-pointer">Cancel</button>
                  <button type="button" onClick={handleSave} disabled={modalLoading} className="px-5 py-2.5 rounded-xl text-sm font-bold text-white bg-ku-purple hover:bg-ku-violet border-none cursor-pointer flex items-center gap-2">
                    {modalLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : editingCollege ? "Update College" : "Add College"}
                  </button>
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      <ConfirmDialog
        open={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDelete}
        title="Delete College"
        message={
          deleteStudentCount > 0
            ? `"${deleteTarget?.name}" has ${deleteStudentCount} associated student${deleteStudentCount === 1 ? "" : "s"}. Continue?`
            : `Are you sure you want to delete "${deleteTarget?.name}"?`
        }
        confirmText="Delete College"
        loading={deleteLoading}
      />
    </div>
  );
};

export default AdminColleges;